import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
  Policy as PolicyIcon,
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';

const DataGovernance: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1B3B6F', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/')}
            sx={{ 
              color: 'white', 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Home
          </Button>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 700, mb: 2, color: 'white' }}>
            Mission-Aligned Governance
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, color: 'white' }}>
            NIST 800-53 Compliant Data Governance Framework
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '0.95rem', mb: 3, maxWidth: '800px', lineHeight: 1.6 }}>
            Policy templates, ATO documentation automation, and continuous compliance monitoring designed specifically for federal agencies.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#1B3B6F',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Key Features */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Governance Capabilities
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <PolicyIcon sx={{ fontSize: 40 }} />,
              title: 'Policy Management',
              desc: 'Pre-built NIST 800-53 policy templates with automated enforcement and exception tracking'
            },
            {
              icon: <SecurityIcon sx={{ fontSize: 40 }} />,
              title: 'ATO Documentation',
              desc: 'Automated generation of Authority to Operate documentation and continuous monitoring reports'
            },
            {
              icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
              title: 'Compliance Monitoring',
              desc: 'Real-time compliance dashboards with automated alerts for policy violations'
            },
            {
              icon: <GavelIcon sx={{ fontSize: 40 }} />,
              title: 'Risk Management',
              desc: 'Integrated risk assessment tools aligned with federal risk management frameworks'
            }
          ].map((feature, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Box sx={{ color: 'secondary.main', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Compliance Standards */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Federal Compliance Standards
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                  NIST 800-53
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Full compliance with NIST security and privacy controls
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                  FISMA
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Federal Information Security Management Act compliance
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                  FedRAMP
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Federal Risk and Authorization Management Program ready
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Why Choose E-Unify Governance?
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <List>
              {[
                'Reduce ATO preparation time by 60%',
                'Automated compliance reporting',
                'Built-in federal policy templates',
                'Continuous monitoring and alerts'
              ].map((item, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: 'secondary.main' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item}
                    primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '1.1rem' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              {[
                'Role-based access controls',
                'Audit trail and change tracking',
                'Integration with existing GRC tools',
                'Expert federal compliance support'
              ].map((item, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: 'secondary.main' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item}
                    primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '1.1rem' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="lg">
          <Card sx={{ p: 4, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.75rem' } }}>
              Strengthen Your Data Governance
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Trusted by federal agencies for mission-critical governance
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: '#1B3B6F',
                color: 'white',
                px: 6,
                py: 2,
                '&:hover': { bgcolor: '#142A54' }
              }}
            >
              Get Started Today
            </Button>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default DataGovernance;
